"use client"
import Link from "next/link"
import Image from "next/image"
import { useEffect, useState } from "react"
import { useParams } from "next/navigation"
import LeadForm from "@/components/LeadForm"
import SellerLink from "@/components/SellerLink"
import { jsonLdScript } from "@/lib/jsonld"
import ViewingForm from "@/components/ViewingForm"
import Icon from "@/components/ui/Icon"
import SatisfactionWidget from "@/components/SatisfactionWidget"
import { useDataSaver } from "@/lib/hooks/useDataSaver"
import { sellerDisplayName } from "@/lib/accounts/display-name"
import { SITE_URL } from "@/lib/site-url"
import { MAX_TRUST_SCORE } from "@/lib/trust/constants"

interface Listing {
  id: string
  title: string
  price: number
  priceType: string
  suburb: string
  city: string
  address: string
  beds: number
  baths: number
  floorArea: number
  standSize: number
  propertyType: string
  description: string
  photos: { url: string; isPrimary: boolean }[]
  features: { feature: string }[]
  trustScore: { totalScore: number; idVerified: boolean; selfieMatched: boolean; titleDeedStatus: string; phoneVerified: boolean; noDuplicates: boolean; eacActive: boolean } | null
  agent: { id: string; name: string; phone: string | null; image: string | null; calcomLink: string | null; role?: string | null; companyName?: string | null; agent: { eacLicense: string | null; bio: string | null; responseRate: number; avgResponseHours: number | null; totalSales: number; agency?: { id: string; name: string; logoUrl: string | null } | null } | null }
  shareDetails?: {
    roomsAvailable: number
    roomType: string
    furnished: string
    billsIncluded: boolean
    availableFrom: string | null
    minStayMonths: number | null
    studentsWelcome: boolean
    smokingAllowed: boolean
    petsAllowed: boolean
    couplesWelcome: boolean
    genderPref: string
    houseRules: string | null
  } | null
}

export default function PropertyPage() {
  const params = useParams()
  const [listing, setListing] = useState<Listing | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [currentPhoto, setCurrentPhoto] = useState(0)
  const [autoPlay, setAutoPlay] = useState(true)
  const [sidebarTab, setSidebarTab] = useState<"enquiry" | "viewing">("enquiry")
  const [sessionUser, setSessionUser] = useState<any>(null)
  const [alertEmail, setAlertEmail] = useState("")
  const [alertTarget, setAlertTarget] = useState("")
  const [alertSent, setAlertSent] = useState(false)
  const [alertSending, setAlertSending] = useState(false)
  const [alertError, setAlertError] = useState("")
  const [copied, setCopied] = useState(false)
  const [mapLoaded, setMapLoaded] = useState(false)
  const { dataSaver } = useDataSaver()

  useEffect(() => {
    const stored = localStorage.getItem("ze_user")
    const expiry = localStorage.getItem("ze_session_expiry")
    if (stored && expiry && new Date(expiry) > new Date()) setSessionUser(JSON.parse(stored))
  }, [])

  useEffect(() => {
    if (!params?.id) return
    fetch("/api/listings/" + params.id)
      .then(r => r.json())
      .then(d => {
        if (d.success) setListing(d.data)
        else setNotFound(true)
        setLoading(false)
      })
      .catch(() => { setNotFound(true); setLoading(false) })
  }, [params?.id])

  useEffect(() => {
    // Data Saver: don't silently pull each next photo on a 4s timer.
    if (dataSaver || !autoPlay || !listing || listing.photos.length <= 1) return
    // WCAG 2.2.2: content that moves for more than five seconds must be
    // pausable, and a reduced-motion preference must be honoured. This gallery
    // advanced forever with no way to stop it from the keyboard.
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return
    const interval = setInterval(() => {
      setCurrentPhoto(p => (p + 1) % listing.photos.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [autoPlay, listing, dataSaver])

  if (loading) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)" }}>
      <div style={{ fontFamily: "Fraunces, serif", fontSize: 24, color: "var(--navy)", opacity: 0.5 }}>Loading...</div>
    </div>
  )

  if (notFound || !listing) return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--cream)", textAlign: "center", padding: 24 }}>
      <div>
        <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 32, color: "var(--navy)", marginBottom: 8 }}>Listing not found</h1>
        <p style={{ color: "var(--muted)", marginBottom: 24 }}>This listing may have been removed or does not exist.</p>
        <Link href="/listings" className="btn-navy">Browse all listings</Link>
      </div>
    </div>
  )

  const pageUrl = SITE_URL + "/listings/" + listing.id
  // Counted redirect: bumps the seller's daily WhatsApp-click stat, then 302s to
  // wa.me with the message built server-side (see /api/go/whatsapp).
  const waLink = listing.agent?.phone ? `/api/go/whatsapp?listing=${listing.id}` : null

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: listing.title,
    description: listing.description ?? listing.title,
    url: pageUrl,
    // og-image.png does not exist (404); the sized PNG does. Host from SITE
    // so this cannot drift from the canonical origin either.
    image: listing.photos[0]?.url ?? SITE_URL + "/og-image-1200x630.png",
    price: listing.price,
    priceCurrency: "USD",
    address: {
      "@type": "PostalAddress",
      streetAddress: listing.address ?? listing.suburb,
      addressLocality: listing.suburb,
      addressRegion: listing.city,
      addressCountry: "ZW",
    },
    numberOfRooms: listing.beds ?? undefined,
    floorSize: listing.floorArea ? { "@type": "QuantitativeValue", value: listing.floorArea, unitCode: "MTK" } : undefined,
    author: listing.agent ? { "@type": "Person", name: listing.agent.name } : undefined,
  }

  const faqItems: { q: string; a: string }[] = [
    { q: "What is the asking price for this property?", a: "$" + listing.price.toLocaleString() + " USD" + (listing.priceType === "RENT" ? " per month" : "") },
    { q: "Where is this property located?", a: (listing.address ?? listing.suburb) + ", " + listing.city + ", Zimbabwe" },
    ...(listing.beds ? [{ q: "How many bedrooms does this property have?", a: listing.beds + " bedroom" + (listing.beds !== 1 ? "s" : "") }] : []),
    ...(listing.trustScore ? [{ q: "What is the Trust Score for this listing?", a: listing.trustScore.totalScore + "/" + MAX_TRUST_SCORE + ", from the checks Zim Estates performs" }] : []),
  ]

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  }

  const SITE = SITE_URL
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home",                         item: SITE },
      { "@type": "ListItem", position: 2, name: listing.priceType === "SALE" ? "Buy" : "Rent", item: SITE + "/listings" },
      { "@type": "ListItem", position: 3, name: listing.suburb + ", " + listing.city, item: SITE + "/listings?suburb=" + encodeURIComponent(listing.suburb) },
      { "@type": "ListItem", position: 4, name: listing.title,                  item: pageUrl },
    ],
  }

  function copyLink() {
    navigator.clipboard.writeText(pageUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(faqLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdScript(breadcrumbLd) }} />
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "16px 24px", fontSize: 13, color: "var(--muted)", display: "flex", gap: 6, flexWrap: "wrap", rowGap: 2 }}>
        <Link href="/">Home</Link> <span>·</span>
        <Link href="/listings">{listing.priceType === "SALE" ? "Buy" : "Rent"}</Link> <span>·</span>
        <Link href={"/listings?suburb=" + listing.suburb}>{listing.suburb}</Link> <span>·</span>
        <span>{listing.title.substring(0, 40)}</span>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 24px" }}>
        <div style={{ background: "#0e1629", borderRadius: 16, overflow: "hidden", position: "relative" }}>
          {listing.photos.length > 0 ? (
            <>
              <div style={{ position: "relative", width: "100%", height: "min(62vh, 520px, 92vw)" }}>
                {/* The photo's own colours fill the letterbox instead of flat navy */}
                <Image src={listing.photos[currentPhoto].url} alt="" aria-hidden fill sizes="100vw" quality={20} style={{ objectFit: "cover", filter: "blur(28px)", transform: "scale(1.15)", opacity: 0.45 }} />
                <Image src={listing.photos[currentPhoto].url} alt={listing.title + " in " + listing.suburb} fill priority={currentPhoto === 0} quality={dataSaver ? 45 : 75} style={{ objectFit: "contain", transition: "opacity 0.5s ease" }} />
              </div>
              {listing.photos.length > 1 && (
                <>
                  <button aria-label="Previous photo" onClick={() => { setCurrentPhoto(p => (p - 1 + listing.photos.length) % listing.photos.length); setAutoPlay(false) }} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "white", borderRadius: "50%", width: 44, height: 44, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{"<"}</button>
                  <button aria-label="Next photo" onClick={() => { setCurrentPhoto(p => (p + 1) % listing.photos.length); setAutoPlay(false) }} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.45)", border: "none", color: "white", borderRadius: "50%", width: 44, height: 44, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{">"}</button>
                  {/* The dots were nameless, stateless buttons: a screen reader
                      announced N identical "button"s with no way to tell which
                      photo was showing. */}
                  <div role="tablist" aria-label="Choose photo" style={{ position: "absolute", bottom: 12, left: "50%", transform: "translateX(-50%)", display: "flex", gap: 6 }}>
                    {listing.photos.map((_: any, i: number) => (
                      <button
                        key={i}
                        role="tab"
                        aria-label={`Photo ${i + 1} of ${listing.photos.length}`}
                        aria-selected={i === currentPhoto}
                        onClick={() => { setCurrentPhoto(i); setAutoPlay(false) }}
                        style={{ width: i === currentPhoto ? 20 : 8, height: 8, borderRadius: 4, background: i === currentPhoto ? "white" : "rgba(255,255,255,0.5)", border: "none", cursor: "pointer", padding: 0, transition: "all 0.3s" }}
                      />
                    ))}
                  </div>
                  {/* WCAG 2.2.2 needs an explicit control, not just "clicking a
                      dot happens to stop it". */}
                  <button
                    onClick={() => setAutoPlay(a => !a)}
                    aria-pressed={!autoPlay}
                    style={{ position: "absolute", bottom: 10, right: 12, background: "rgba(0,0,0,0.45)", color: "white", border: "none", borderRadius: 20, fontSize: 11, fontWeight: 600, padding: "4px 10px", cursor: "pointer" }}
                  >
                    {autoPlay ? "Pause slideshow" : "Play slideshow"}
                  </button>
                  <div style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.45)", color: "white", fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 20 }}>{currentPhoto + 1} / {listing.photos.length}</div>
                </>
              )}
            </>
          ) : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "rgba(253,251,247,0.5)", fontSize: 14 }}>No photos yet</div>}
        </div>
      </div>

      <div className="mob-listing-detail" style={{ maxWidth: 1280, margin: "0 auto", padding: "32px 24px", gap: 48 }}>
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            <span style={{ background: "var(--gold)", color: "var(--navy)", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 6, textTransform: "uppercase" }}>{listing.priceType === "RENT" ? "For Rent" : "For Sale"}</span>
            {listing.trustScore && <span style={{ background: "rgba(61,139,95,0.1)", color: "var(--green-text)", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 6, border: "1px solid rgba(61,139,95,0.2)" }}>Trust Score {listing.trustScore.totalScore}/{MAX_TRUST_SCORE}</span>}
          </div>

          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 36, fontWeight: 700, color: "var(--navy)", lineHeight: 1.2, marginBottom: 8 }}>{listing.title}</h1>
          <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24 }}>
            {sessionUser ? (listing.address ?? listing.suburb + ", " + listing.city) : listing.suburb + ", " + listing.city}
            {!sessionUser && (
              <Link href={"/auth/login?next=/listings/" + listing.id} style={{ marginLeft: 8, fontSize: 12, color: "var(--gold-deep)", fontWeight: 600, textDecoration: "underline" }}>Sign in to see full address</Link>
            )}
          </p>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "Fraunces, serif", fontSize: 40, fontWeight: 700, color: "var(--navy)" }}>
              ${listing.price.toLocaleString()} <span style={{ fontSize: 16, color: "var(--muted)", fontWeight: 400 }}>{listing.priceType === "RENT" ? "/month" : "USD"}</span>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginRight: 4 }}>Share</span>
            <a className="share-pill" href={"https://wa.me/?text=" + encodeURIComponent(listing.title + ", " + pageUrl)} target="_blank" rel="noopener noreferrer"><i style={{ background: "#25D366" }} />WhatsApp</a>
            <a className="share-pill" href={"https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(pageUrl)} target="_blank" rel="noopener noreferrer"><i style={{ background: "#1877f2" }} />Facebook</a>
            <a className="share-pill" href={"https://x.com/intent/tweet?url=" + encodeURIComponent(pageUrl) + "&text=" + encodeURIComponent(listing.title)} target="_blank" rel="noopener noreferrer"><i style={{ background: "#000" }} />X</a>
            <a className="share-pill" href={"https://www.linkedin.com/sharing/share-offsite/?url=" + encodeURIComponent(pageUrl)} target="_blank" rel="noopener noreferrer"><i style={{ background: "#0a66c2" }} />LinkedIn</a>
            <button className={"share-pill" + (copied ? " done" : "")} onClick={copyLink}>{copied ? "Copied" : "Copy link"}</button>
          </div>

          {listing.priceType === "SALE" && (
            <div style={{ background: "rgba(27,45,84,0.04)", border: "1px solid var(--line)", borderRadius: 12, padding: 20, marginBottom: 32 }}>
              {alertSent ? (
                <div style={{ fontSize: 13, color: "var(--green-text)", fontWeight: 600 }}>Alert set. We will email you if the price drops to your target.</div>
              ) : (
                <>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Alert me if price drops</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <input value={alertEmail} onChange={e => setAlertEmail(e.target.value)} placeholder="your@email.com" type="email" style={{ flex: 2, minWidth: 140, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                    <input value={alertTarget} onChange={e => setAlertTarget(e.target.value)} placeholder={"Target $" + Math.round(listing.price * 0.95).toLocaleString()} type="number" style={{ flex: 1, minWidth: 100, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "var(--cream)", outline: "none" }} />
                    <button disabled={alertSending} onClick={async () => {
                      if (!alertEmail) { setAlertError("Please enter your email address"); return }
                      setAlertSending(true); setAlertError("")
                      try {
                        const res = await fetch("/api/alerts", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ listingId: listing.id, subscriberEmail: alertEmail, targetPrice: alertTarget ? Number(alertTarget) : Math.round(listing.price * 0.95) }) })
                        const data = await res.json()
                        if (data.success) setAlertSent(true)
                        else setAlertError(data.error ?? "Something went wrong. Please try again.")
                      } catch { setAlertError("Network error. Please try again.") }
                      setAlertSending(false)
                    }} style={{ padding: "10px 18px", background: alertSending ? "var(--muted)" : "var(--navy)", color: "var(--cream)", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: alertSending ? "not-allowed" : "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
                      {alertSending ? "..." : "Set alert"}
                    </button>
                  </div>
                  {alertError && <div style={{ fontSize: 12, color: "var(--red-text)", marginTop: 8 }}>{alertError}</div>}
                </>
              )}
            </div>
          )}

          {listing.trustScore && (
            <div style={{ background: "rgba(61,139,95,0.05)", border: "1px solid rgba(61,139,95,0.2)", borderRadius: 14, padding: 24, marginBottom: 32 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ color: "var(--green-text)" }}><Icon name="Verified" size={20} /></span>
                <span style={{ fontWeight: 700, color: "var(--green-text)", fontSize: 15 }}>Trust Score: {listing.trustScore.totalScore}/{MAX_TRUST_SCORE}</span>
              </div>
              {(() => {
                const isOwnerListing = !listing.agent || !listing.agent.agent?.eacLicense
                const checks = [
                  { label: "National ID verified", val: listing.trustScore.idVerified, neutral: false },
                  // Earned when a person on our team approves the KYC after comparing
                  // the selfie with the ID photo (see lib/trust/rules.ts). It is a
                  // human comparison, not an automated face match.
                  { label: "Selfie matched to ID", val: listing.trustScore.selfieMatched, neutral: false },
                  { label: "Phone verified", val: listing.trustScore.phoneVerified, neutral: false },
                  // "active" implied a check against the EACZ register; the number
                  // is free text the agent types in and is never validated.
                  ...(!isOwnerListing ? [{ label: "EAC license on file (self-declared)", val: listing.trustScore.eacActive, neutral: false }] : []),
                  // It said "pending review" for listings with no deed at all.
                  { label: listing.trustScore.titleDeedStatus === "VERIFIED" ? "Title deed checked by our team" : listing.trustScore.titleDeedStatus === "UPLOADED" ? "Title deed (pending review)" : "Title deed not provided", val: listing.trustScore.titleDeedStatus === "VERIFIED", neutral: listing.trustScore.titleDeedStatus !== "VERIFIED" },
                ]
                return (
                  <>
                    <div className="mob-grid-2" style={{ gap: 8 }}>
                      {checks.map(check => (
                        <div key={check.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                          <span style={{ color: check.val ? "var(--green)" : "var(--muted)" }}>{check.val ? "✓" : "·"}</span>
                          <span style={{ color: check.val ? "var(--ink)" : "var(--muted)" }}>{check.label}</span>
                        </div>
                      ))}
                    </div>
                    {isOwnerListing && (
                      <div style={{ marginTop: 12, fontSize: 12, color: "var(--muted)", lineHeight: 1.5, borderTop: "1px solid rgba(61,139,95,0.15)", paddingTop: 10 }}>
                        Private sale, listed directly by the property owner. EAC licensing applies to agents only.
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          )}

          <div className="mob-grid-4" style={{ gap: 16, marginBottom: 32 }}>
            {[
              { icon: "Beds", value: listing.beds, label: "Bedrooms" },
              { icon: "Bath", value: listing.baths, label: "Bathrooms" },
              { icon: "Size", value: listing.floorArea ? listing.floorArea + "m²" : "N/A", label: "Floor area" },
              { icon: "Home", value: listing.propertyType, label: "Type" },
            ].map(spec => (
              <div key={spec.label} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 12, padding: 16, textAlign: "center" }}>
                <div style={{ marginBottom: 8, color: "var(--navy)" }}><Icon name={spec.icon} size={24} /></div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 600, color: "var(--navy)" }}>{spec.value}</div>
                <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 4 }}>{spec.label}</div>
              </div>
            ))}
          </div>

          {listing.description && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--navy)", marginBottom: 12 }}>About this property</h2>
              <p style={{ fontSize: 14, color: "var(--ink)", lineHeight: 1.7 }}>{listing.description}</p>
            </div>
          )}

          {/* ── Room Details card (Room to Let listings only) ── */}
          {listing.propertyType === "ROOM" && listing.shareDetails && (() => {
            const sd = listing.shareDetails
            const roomTypeLabel: Record<string, string> = { SINGLE: "Single", DOUBLE: "Double", ENSUITE: "En-suite", SHARED: "Shared" }
            const furnishedLabel: Record<string, string> = { FURNISHED: "Fully furnished", UNFURNISHED: "Unfurnished", PART_FURNISHED: "Part furnished" }
            const stayLabel = sd.minStayMonths == null ? "Flexible" : sd.minStayMonths === 1 ? "1 month" : sd.minStayMonths === 12 ? "1 year+" : `${sd.minStayMonths} months`
            const availLabel = sd.availableFrom ? new Date(sd.availableFrom).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "Available now"
            const genderLabel: Record<string, string> = { ANY: "Any gender", FEMALE_ONLY: "Female only", MALE_ONLY: "Male only" }
            const rules: { icon: string; label: string; on: boolean }[] = [
              { icon: "🎓", label: "Students welcome", on: sd.studentsWelcome },
              { icon: "👫", label: "Couples welcome", on: sd.couplesWelcome },
              { icon: "🚬", label: "Smoking allowed", on: sd.smokingAllowed },
              { icon: "🐾", label: "Pets allowed", on: sd.petsAllowed },
            ]
            return (
              <div style={{ background: "var(--cream-warm)", border: "1.5px solid var(--gold)", borderRadius: 14, padding: "22px 20px", marginBottom: 32 }}>
                <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 20, fontWeight: 700, color: "var(--navy)", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                  🛏️ Room Details &amp; House Rules
                </h2>
                {/* Specs grid */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 20 }}>
                  {[
                    [sd.roomsAvailable.toString(), "Room" + (sd.roomsAvailable > 1 ? "s" : "") + " available"],
                    [roomTypeLabel[sd.roomType] ?? sd.roomType, "Room type"],
                    [furnishedLabel[sd.furnished] ?? sd.furnished, "Furnished"],
                    [sd.billsIncluded ? "✅ Included" : "Not included", "Bills"],
                    [availLabel, "Available from"],
                    [stayLabel, "Min stay"],
                  ].map(([val, key]) => (
                    <div key={key} style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 9, padding: "11px 12px", textAlign: "center" }}>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 14, fontWeight: 700, color: "var(--navy)" }}>{val}</div>
                      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{key}</div>
                    </div>
                  ))}
                </div>
                {/* Rule pills */}
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>House rules</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: sd.genderPref !== "ANY" || sd.houseRules ? 16 : 0 }}>
                  {rules.map(r => (
                    <span key={r.label} style={{
                      display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 18, fontSize: 12, fontWeight: 700,
                      background: r.on ? "rgba(22,163,74,0.1)" : "rgba(220,38,38,0.07)",
                      color: r.on ? "var(--green)" : "var(--red)",
                      border: `1px solid ${r.on ? "rgba(22,163,74,0.2)" : "rgba(220,38,38,0.15)"}`,
                    }}>
                      {r.icon} {r.on ? r.label : "No " + r.label.replace(" welcome", "").replace("allowed", "").trim()}
                    </span>
                  ))}
                  {sd.genderPref !== "ANY" && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 12px", borderRadius: 18, fontSize: 12, fontWeight: 700, background: "rgba(219,39,119,0.08)", color: "#be185d", border: "1px solid rgba(219,39,119,0.15)" }}>
                      👤 {genderLabel[sd.genderPref]}
                    </span>
                  )}
                </div>
                {/* Free-text rules */}
                {sd.houseRules && (
                  <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 9, padding: "13px 15px", fontSize: 13, color: "var(--ink)", lineHeight: 1.7, fontStyle: "italic" }}>
                    &ldquo;{sd.houseRules}&rdquo;
                  </div>
                )}
              </div>
            )
          })()}

          {listing.features.length > 0 && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 22, fontWeight: 600, color: "var(--navy)", marginBottom: 16 }}>Features and amenities</h2>
              <div className="mob-grid-3" style={{ gap: 10 }}>
                {listing.features.map(f => (
                  <div key={f.feature} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: "var(--cream-warm)", borderRadius: 8, fontSize: 13 }}>
                    <span style={{ color: "var(--green-text)" }}>✓</span> {f.feature}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suburb price comparison suspended until sufficient data */}

          <div style={{ background: "rgba(200,150,62,0.06)", border: "1px solid rgba(200,150,62,0.2)", borderRadius: 14, padding: 18, marginBottom: 32 }}>
            <div style={{ fontWeight: 700, color: "var(--gold-deep)", marginBottom: 6 }}>Always do due diligence before paying</div>
            <div style={{ fontSize: 13, color: "var(--ink)", lineHeight: 1.5 }}>Never pay a deposit before your conveyancer confirms the Deeds Office search is clean. <Link href="/trust-centre" style={{ color: "var(--gold-deep)", fontWeight: 600 }}>Read our safety guide {'>'}</Link></div>
          </div>

          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 12 }}>Approximate location</div>
            {/* Without an embed key the iframe is a blank 220px box (Google answers
                401), which is what production showed. Fall back to a plain
                location line with a working Maps link instead. */}
            {!process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "14px 16px", background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--ink)", fontSize: 14 }}>
                  <span style={{ color: "var(--gold-deep)" }}><Icon name="Location" size={18} /></span>
                  <span><strong style={{ color: "var(--navy)" }}>{listing.suburb}</strong>, {listing.city}</span>
                </div>
                <a href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(listing.suburb + " " + listing.city + " Zimbabwe")} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "var(--gold-deep)", textDecoration: "none" }}>Open in Google Maps {'>'}</a>
              </div>
            ) : dataSaver && !mapLoaded ? (
              <button
                onClick={() => setMapLoaded(true)}
                style={{ width: "100%", height: 220, border: "1px dashed var(--line)", borderRadius: 12, background: "rgba(27,45,84,0.04)", color: "var(--muted)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                Tap to load map (saves data)
              </button>
            ) : (
              <iframe
                src={`https://www.google.com/maps/embed/v1/place?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(listing.suburb + " " + listing.city + " Zimbabwe")}`}
                style={{ width: "100%", height: 220, border: 0, borderRadius: 12, display: "block" }}
                title="Approximate property location"
                loading="lazy"
                allowFullScreen
              />
            )}
          </div>

          <SatisfactionWidget listingId={listing.id} />
        </div>

        <div style={{ position: "sticky", top: 88 }}>
          <div style={{ background: "var(--cream)", border: "1px solid var(--line)", borderRadius: 20, padding: 28, marginBottom: 16 }}>
            {/* Agent header, always visible */}
            {/* The seller's name is the natural way into their other properties,
                and it was plain text. It now links to their profile — which works
                for FSBO owners and developers too, not just agents. */}
            <div style={{ display: "flex", gap: 14, alignItems: "center", marginBottom: 20, paddingBottom: 20, borderBottom: "1px solid var(--line)" }}>
              <SellerLink agent={listing.agent} style={{ display: "flex", gap: 14, alignItems: "center", textDecoration: "none", flex: 1, minWidth: 0 }}>
                <div style={{ width: 52, height: 52, borderRadius: "50%", background: "linear-gradient(135deg,var(--navy),var(--navy-soft))", display: "grid", placeItems: "center", fontWeight: 700, color: "var(--cream)", fontSize: 16, flexShrink: 0 }}>
                  {sellerDisplayName(listing.agent, "ZE").substring(0, 2).toUpperCase()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 600, color: "var(--navy)" }}>{sellerDisplayName(listing.agent, "Verified Agent")}</div>
                  {listing.agent?.agent?.eacLicense && <div style={{ fontSize: 12, color: "var(--muted)" }}>EAC #{listing.agent.agent.eacLicense}</div>}
                  {listing.agent?.agent?.avgResponseHours != null && listing.agent.agent.responseRate > 0 && (
                    <div style={{ fontSize: 12, color: "var(--green-text)", marginTop: 2 }}>Responds within {listing.agent.agent.avgResponseHours}h</div>
                  )}
                  {listing.agent?.id && (
                    <div style={{ fontSize: 12, color: "var(--gold-deep)", fontWeight: 600, marginTop: 4 }}>View all their properties {'>'}</div>
                  )}
                </div>
              </SellerLink>
            </div>

            {/* Anchor target for the contact affordance on the search grid, which
                cannot deep-link to WhatsApp because public browse omits the phone. */}
            <div id="contact" style={{ scrollMarginTop: 90 }} />

            {sessionUser ? (
              <>
                {listing.agent?.calcomLink ? (
                  <div style={{ marginBottom: 20 }}>
                    <LeadForm listingId={listing.id} listingTitle={listing.title} />
                    <a href={listing.agent.calcomLink} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 14, background: "var(--gold)", color: "var(--navy)", borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: "none", marginTop: 12 }}>Book a viewing</a>
                  </div>
                ) : (
                  <>
                    <div role="tablist" aria-label="Contact options" style={{ display: "flex", gap: 6, marginBottom: 20 }}>
                      <button role="tab" aria-selected={sidebarTab === "enquiry"} aria-controls="panel-enquiry" id="tab-enquiry" onClick={() => setSidebarTab("enquiry")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: "1px solid " + (sidebarTab === "enquiry" ? "var(--navy)" : "var(--line)"), background: sidebarTab === "enquiry" ? "var(--navy)" : "var(--cream)", color: sidebarTab === "enquiry" ? "var(--cream)" : "var(--navy)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Enquiry</button>
                      <button role="tab" aria-selected={sidebarTab === "viewing"} aria-controls="panel-viewing" id="tab-viewing" onClick={() => setSidebarTab("viewing")} style={{ flex: 1, padding: "9px", borderRadius: 8, border: "1px solid " + (sidebarTab === "viewing" ? "var(--navy)" : "var(--line)"), background: sidebarTab === "viewing" ? "var(--navy)" : "var(--cream)", color: sidebarTab === "viewing" ? "var(--cream)" : "var(--navy)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Book a Viewing</button>
                    </div>
                    {sidebarTab === "enquiry" ? (
                      <div role="tabpanel" id="panel-enquiry" aria-labelledby="tab-enquiry">
                        <LeadForm listingId={listing.id} listingTitle={listing.title} />
                      </div>
                    ) : (
                      <div role="tabpanel" id="panel-viewing" aria-labelledby="tab-viewing">
                        <ViewingForm listingId={listing.id} listingTitle={listing.title} />
                      </div>
                    )}
                  </>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
                  {waLink && <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: "none" }}>WhatsApp agent</a>}
                  {listing.agent?.phone && <a href={"tel:" + listing.agent.phone} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px", border: "1px solid var(--line)", borderRadius: 10, fontWeight: 600, fontSize: 15, color: "var(--navy)", textDecoration: "none" }}>Call agent</a>}
                  <Link href={"/messages/" + listing.id} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px", border: "1px solid var(--line)", borderRadius: 10, fontWeight: 600, fontSize: 15, color: "var(--navy)", textDecoration: "none" }}>Message agent</Link>
                </div>
              </>
            ) : (
              /* Login gate */
              <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
                <div style={{ marginBottom: 12, color: "var(--gold-deep)", display: "flex", justifyContent: "center" }}><Icon name="Shield" size={30} /></div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 17, fontWeight: 700, color: "var(--navy)", marginBottom: 8 }}>Sign in to contact this agent</div>
                <p style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6, marginBottom: 20 }}>
                  Create a free account to get the agent's WhatsApp, phone number, and send enquiries.
                </p>
                <Link href={"/auth/login?next=/listings/" + listing.id} style={{ display: "block", padding: "14px", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, fontWeight: 700, fontSize: 15, textDecoration: "none", marginBottom: 10 }}>
                  Sign in
                </Link>
                <Link href={"/auth/register?next=/listings/" + listing.id} style={{ display: "block", padding: "14px", border: "1px solid var(--line)", color: "var(--navy)", borderRadius: 10, fontWeight: 600, fontSize: 15, textDecoration: "none" }}>
                  Create free account
                </Link>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 14, lineHeight: 1.5 }}>
                  Free to join · Takes 30 seconds · No spam
                </div>
              </div>
            )}
          </div>
          <div style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 14, padding: 18, marginBottom: 16, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 8 }}>Need a conveyancer?</div>
            <div style={{ color: "var(--muted)", marginBottom: 12, lineHeight: 1.5 }}>Browse verified Zimbabwe lawyers for safe transfer.</div>
            <Link href="/services" className="btn-outline" style={{ fontSize: 12 }}>View specialists {'>'}</Link>
          </div>
          <div style={{ textAlign: "center" }}>
            <Link href={`/report-listing?id=${listing.id}`} style={{ fontSize: 12, color: "var(--red-text)", display: "inline-flex", alignItems: "center", gap: 4 }}>Report this listing</Link>
          </div>
        </div>
      </div>

      <div className="listing-cta-bar" style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "var(--cream)", borderTop: "1px solid var(--line)", padding: "12px 16px", display: "flex", gap: 10, zIndex: 40 }}>
        {sessionUser ? (
          <>
            {waLink ? (
              <a href={waLink} target="_blank" rel="noopener noreferrer" style={{ flex: 1, padding: "12px", background: "#075E54", color: "white", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center", display: "block", textDecoration: "none" }}>WhatsApp agent</a>
            ) : listing.agent?.id ? (
              <Link href={"/agents/" + listing.agent.id} style={{ flex: 1, padding: "12px", background: "var(--cream-warm)", color: "var(--navy)", border: "1px solid var(--line)", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center", display: "block", textDecoration: "none" }}>Seller profile</Link>
            ) : null}
            {listing.agent?.phone && <a href={"tel:" + listing.agent.phone} style={{ flex: 1, padding: "12px", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center", display: "block", textDecoration: "none" }}>Call</a>}
          </>
        ) : (
          <>
            <Link href={"/auth/login?next=/listings/" + listing.id} style={{ flex: 1, padding: "12px", background: "var(--navy)", color: "var(--cream)", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center", display: "block", textDecoration: "none" }}>Sign in to contact agent</Link>
            <Link href={"/auth/register?next=/listings/" + listing.id} style={{ flex: 1, padding: "12px", background: "var(--gold)", color: "var(--navy)", borderRadius: 10, fontWeight: 700, fontSize: 14, textAlign: "center", display: "block", textDecoration: "none" }}>Create free account</Link>
          </>
        )}
      </div>
    </div>
  )
}
